import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Circle } from 'lucide-react';
import { hasAnyRole } from '@court-sync/shared';
import { useAuthStore } from '@/store/auth';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { useOrgHost } from '@/contexts/OrgHostContext';
import { usePublicOrgProfile } from '@/queries/publicOrgProfile';
import { usePublicCatalog } from '@/queries/publicCatalog';
import { PublicOrgRouteShell } from '@/components/public/PublicOrgRouteShell';
import { OrgCourtHireGrid } from '@/components/public/org/OrgCourtHireGrid';
import { OrgLessonsSection, OrgCoachesSection } from '@/components/public/org/OrgLessonsSection';
import { OrgMembershipsSection } from '@/components/public/org/OrgMembershipsSection';
import { OrgPlayEventsSection } from '@/components/public/org/OrgPlayEventsSection';
import { OrgAboutSection } from '@/components/public/org/OrgAboutSection';
import { OrgSidebar } from '@/components/public/org/OrgSidebar';
import { OrgNearbyStrip } from '@/components/public/org/OrgNearbyStrip';
import { OrgConsumerHome } from '@/components/public/org/OrgConsumerHome';
import { OrgPublicBanner } from '@/components/public/org/OrgPublicBanner';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { BrowserImage } from '@/components/ui/BrowserImage';
import { usePublicBookingStore } from '@/store/publicBooking';
import {
  appendStudentInviteQuery,
  resolveManualInviteTokenForNavigation,
} from '@/pages/public/publicBooking/bookingPaths';
import { buildOrgAbsoluteUrl } from '@/lib/orgPaths';
import { buildCourtHireBookingSearchParams } from '@/lib/courtHirePrefill';

function PublicOrgPageContent() {
  const orgSlug = useOrgSlug();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const consumerNav = useUnifiedConsumerNav();
  const isConsumerRole = Boolean(user && hasAnyRole(user, ['guardian', 'student', 'member']));
  const [searchParams] = useSearchParams();
  const { host, academy: hostAcademy, loading: hostLoading } = useOrgHost();
  const { profile, isLoading: profileLoading, error } = usePublicOrgProfile({
    slug: orgSlug,
    subdomainLabel: host.mode === 'subdomain' ? host.subdomainLabel : undefined,
    hostLoading: host.mode === 'subdomain' && hostLoading,
    enabled: !!orgSlug || (host.mode === 'subdomain' && !!host.subdomainLabel),
  });

  const manualInviteToken = usePublicBookingStore((s) => s.manualInviteToken);
  const captureManualInviteFromSearchParams = usePublicBookingStore(
    (s) => s.captureManualInviteFromSearchParams
  );

  useEffect(() => {
    captureManualInviteFromSearchParams(searchParams);
  }, [searchParams, captureManualInviteFromSearchParams]);

  const resolvedManualInviteToken = useMemo(
    () => resolveManualInviteTokenForNavigation(searchParams, manualInviteToken),
    [searchParams, manualInviteToken]
  );

  const academySlug =
    profile?.academySlug ??
    hostAcademy?.slug ??
    (profile?.modules.hasAcademy && orgSlug ? orgSlug : undefined);

  const suppressPublicCatalog = Boolean(
    isConsumerRole &&
      (consumerNav.useUnifiedNav
        ? consumerNav.hasLessonsAccess || consumerNav.hasClubMemberAccess
        : true)
  );

  const catalogEnabled = Boolean(
    profile?.modules.hasAcademy && academySlug && !suppressPublicCatalog
  );

  const {
    academy,
    lessons,
    terms,
    academySports,
    coaches,
    ageGroups,
    publicLocations,
    publicPrograms,
    proRataPreviewByLessonId,
    isLoading: catalogLoading,
  } = usePublicCatalog({
    slug: academySlug,
    subdomainLabel: host.mode === 'subdomain' ? host.subdomainLabel : undefined,
    seedAcademy: profile?.academy ?? (host.mode === 'subdomain' ? hostAcademy ?? undefined : undefined),
    hostLoading: host.mode === 'subdomain' && hostLoading,
    includeCoaches: true,
    enabled: catalogEnabled,
  });

  const resolvedAcademy = academy ?? profile?.academy ?? hostAcademy;

  const { p } = useOrgPaths(profile?.organization);

  const orgPath = useCallback(
    (subpath: string) => {
      if (p) return p(subpath);
      if (orgSlug) return `/club-academy/${orgSlug}${subpath}`;
      return subpath;
    },
    [p, orgSlug]
  );

  const [failedBannerUrl, setFailedBannerUrl] = useState<string | null>(null);
  const bannerUrl = profile?.organization.bannerUrl;
  const showBanner = Boolean(bannerUrl && bannerUrl !== failedBannerUrl);

  const showConsumerHome = Boolean(
    consumerNav.useUnifiedNav &&
      (consumerNav.hasLessonsAccess || consumerNav.hasClubMemberAccess)
  );

  useEffect(() => {
    if (!profile?.organization) return undefined;
    const canonical = buildOrgAbsoluteUrl(profile.organization, '');
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link') as HTMLLinkElement;
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = canonical;
    return () => {
      link?.remove();
    };
  }, [profile?.organization]);

  const handleBookLesson = (lessonId: string) => {
    navigate(
      appendStudentInviteQuery(orgPath(`/book/${lessonId}`), resolvedManualInviteToken)
    );
  };

  const handleCourtHireSlotSelect = (
    courtId: string,
    date: string,
    startTime: string,
    endTime: string
  ) => {
    const params = buildCourtHireBookingSearchParams(courtId, date, startTime, endTime);
    navigate(`${orgPath('/court-hire')}?${params.toString()}`);
  };

  const loading = profileLoading || (catalogEnabled && catalogLoading);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center p-6 text-center">
        <Circle className="mb-4 h-12 w-12 text-muted-foreground" aria-hidden />
        <h1 className="text-xl font-semibold">Organization not found</h1>
      </div>
    );
  }

  const { organization, modules } = profile;

  if (showConsumerHome) {
    return (
      <>
        <OrgPublicBanner profile={profile} />
        <OrgConsumerHome />
      </>
    );
  }

  return (
    <>
      {showBanner ? (
        <div className="relative h-40 w-full overflow-hidden bg-muted sm:h-56 lg:h-64">
          <BrowserImage
            src={bannerUrl!}
            alt=""
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
            onError={() => setFailedBannerUrl(bannerUrl!)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 text-white">
            <div className="mx-auto max-w-7xl px-4 pb-6 sm:px-6 lg:px-8">
              <h1 className="text-3xl font-bold sm:text-4xl">{organization.name}</h1>
              {organization.description ? (
                <p className="mt-2 line-clamp-2 max-w-2xl text-sm text-white/90 sm:text-base">
                  {organization.description}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <header className="border-b border-border bg-card px-4 py-8 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-foreground">{organization.name}</h1>
        </header>
      )}

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_20rem] xl:grid-cols-[1fr_22rem]">
          <div className="min-w-0 space-y-12">
            {modules.courtHireEntitled && orgSlug ? (
              <OrgCourtHireGrid
                orgSlug={orgSlug}
                sports={profile.sports}
                onSlotSelect={handleCourtHireSlotSelect}
              />
            ) : null}

            {modules.hasAcademy && resolvedAcademy && academySlug ? (
              <OrgLessonsSection
                academy={resolvedAcademy}
                academySlug={academySlug}
                lessons={lessons}
                terms={terms}
                academySports={academySports}
                ageGroups={ageGroups}
                publicLocations={publicLocations}
                publicPrograms={publicPrograms}
                proRataPreviewByLessonId={proRataPreviewByLessonId}
                resolvedManualInviteToken={resolvedManualInviteToken}
                buildOrgPath={orgPath}
                onBookLesson={handleBookLesson}
                onViewAll={() => navigate(orgPath('/lessons'))}
              />
            ) : null}

            {modules.playEventsEntitled && orgSlug ? (
              <OrgPlayEventsSection
                orgSlug={orgSlug}
                orgPath={orgPath}
                playEventsEntitled={modules.playEventsEntitled}
              />
            ) : null}

            <OrgMembershipsSection
              orgPath={orgPath}
              membershipJoinAvailable={modules.membershipOnlineJoinEnabled}
              membershipsEntitled={modules.membershipsEntitled}
            />

            <OrgAboutSection organization={organization} />

            {modules.hasAcademy ? (
              <OrgCoachesSection
                coaches={coaches}
                showCoaches={organization.showCoachesOnPublicPage !== false}
              />
            ) : null}
          </div>

          <OrgSidebar
            organization={organization}
            amenities={profile.amenities}
            sports={profile.sports}
            locations={profile.locations}
            operatingHours={profile.operatingHours}
          />
        </div>
      </div>

      {orgSlug ? <OrgNearbyStrip orgSlug={orgSlug} /> : null}
    </>
  );
}

export default function PublicOrgPage() {
  return (
    <PublicOrgRouteShell>
      <PublicOrgPageContent />
    </PublicOrgRouteShell>
  );
}
